import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, LockIcon, PhoneIcon, RefreshCwIcon } from 'lucide-react';

/**
 * DESIGN ONLY — presentation for the existing WebsiteForge project inquiry form.
 *
 * IMPLEMENTATION NOTE: when this design is transferred, the live form must be preserved exactly:
 * the existing action/embed, field names and IDs, hidden fields, routing, notifications, webhooks,
 * analytics and conversion tracking, the real reCAPTCHA, and production validation/success behavior.
 * The checkbox and success message below are prototype-only and MUST NOT be copied into production.
 *
 * TODO (content): verify 231-266-0606 against the production source before launch.
 */

const steps = [{
  title: 'Initial review',
  body: 'We review the business, current site, requirements, and systems involved.'
}, {
  title: 'Working conversation',
  body: 'We talk through priorities, integrations, constraints, and success measures.'
}, {
  title: 'Written scope',
  body: 'You receive a proposed scope, timeline, and price before work begins.'
}];
export function GetStarted() {
  const [verified, setVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  return <section id="get-started" className="relative overflow-hidden bg-ink py-14 lg:py-20" data-id="element-66" data-mp="1fwenat2pw20z">
      <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-67" data-mp="1axk61vkeeliz" />
      <div className="absolute -right-24 bottom-0 h-[30rem] w-[30rem] rounded-full bg-forge-orange/[0.09] blur-[130px]" aria-hidden="true" data-id="element-68" data-mp="27nb0jusow8os" />

      <div className="relative mx-auto grid max-w-8xl gap-9 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-10" data-id="element-69" data-mp="23lpr6jfd6cjc">
        <div className="min-w-0 lg:col-span-5" data-id="element-70" data-mp="1ysmysembp94q">
          <p className="font-mono text-[11.5px] uppercase tracking-[0.18em] text-forge-cyan" data-id="element-71" data-mp="1tvwibq6lwbis">
            Start a project conversation
          </p>
          <h2 className="mt-3 font-display text-[1.7rem] font-extrabold leading-[1.12] tracking-[-0.02em] text-white sm:text-[2.05rem]" data-id="element-72" data-mp="2rc3btovmd93w">
            Bring us the business problem. We&apos;ll define the right scope.
          </h2>
          <p className="mt-4 max-w-lg text-[14.5px] leading-relaxed text-mist-200" data-id="element-73" data-mp="3afdrsiln1yq0">
            Share the current situation, the goal, and any systems or workflows involved. A WebsiteForge project lead will
            review it and follow up with the right next step.
          </p>

          <ol className="mt-7 space-y-3.5" data-id="element-74" data-mp="30n7qujdes6yo">
            {steps.map((step, i) => <li key={step.title} className="flex min-w-0 gap-3.5" data-id="element-75" data-mp="2mohxnetpmrfl" data-mpi={i}>
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-white/12 bg-white/[0.04] font-mono text-[12px] text-forge-orange" data-id="element-76" data-mp="2rpxj37zsuzyi">
                  0{i + 1}
                </span>
                <div className="min-w-0" data-id="element-77" data-mp="2hr86nuqoa7pn">
                  <p className="text-[14px] font-bold text-white" data-id="element-78" data-mp="by17bf53rs1v">{step.title}</p>
                  <p className="mt-0.5 text-[13.5px] leading-snug text-mist-300" data-id="element-79" data-mp="bvx654yl975a">{step.body}</p>
                </div>
              </li>)}
          </ol>

          <a href="tel:2312660606" className="mt-7 inline-flex items-center gap-2.5 text-[15px] font-bold text-white transition-colors hover:text-forge-ember" data-id="element-80" data-mp="20i6ysw6y4vqu">
            <PhoneIcon className="h-4 w-4 text-forge-cyan" aria-hidden="true" data-id="element-81" data-mp="2a7yy23pzqd1n" />
            231-266-0606
          </a>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45
      }} className="min-w-0 lg:col-span-7" data-id="element-82" data-mp="25ee0udw81n3i">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy-950/85 p-6 shadow-lift" data-id="element-83" data-mp="2t505dv70op8r">
            <span className="absolute inset-x-0 top-0 h-[3px] bg-forge-orange" aria-hidden="true" data-id="element-84" data-mp="2o7hvo2l6e5zp" />
            <div className="absolute inset-0 bp-fine opacity-50" aria-hidden="true" data-id="element-85" data-mp="ysk1v25lvp7l" />

            <div className="relative mb-5 flex items-center justify-between gap-4 border-b border-white/10 pb-4" data-id="element-86" data-mp="tu8sstn4xiqf">
              <div className="min-w-0" data-id="element-87" data-mp="w72nibt1jsqy">
                <p className="font-display text-[15.5px] font-bold text-white" data-id="element-88" data-mp="108xk53011hye">Project inquiry</p>
                <p className="mt-1 text-[12.5px] text-mist-400" data-id="element-89" data-mp="158afkc4yrxcl">Reviewed by the WebsiteForge team.</p>
              </div>
              <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono text-[11.5px] uppercase tracking-[0.12em] text-mist-300" data-id="element-90" data-mp="1a7n4d2febeht">
                <LockIcon className="h-3 w-3 text-forge-green" aria-hidden="true" data-id="element-91" data-mp="hhy1skn1f6e3" />
                Secure
              </span>
            </div>

            <form className="relative" onSubmit={e => {
            e.preventDefault();
            setSubmitted(true);
          }} data-id="element-92" data-mp="mghpapha4q8k">
              <div className="grid gap-3.5 sm:grid-cols-2" data-id="element-93" data-mp="3pxztmmxuff48">
                <Field id="name" name="name" label="Name" placeholder="Your full name" required data-id="element-94" data-mp="3ux2t7rv20t4t" />
                <Field id="email" name="email" type="email" label="Email" placeholder="you@company.com" required data-id="element-95" data-mp="3nempsxdonfwu" />
                <Field id="mobile" name="mobile" type="tel" label="Phone" placeholder="(000) 000-0000" required data-id="element-96" data-mp="3ig1p6wuiqu7v" />
                <Field id="company" name="company" label="Company" placeholder="Business name" data-id="element-97" data-mp="3dem6vcjq71t6" />
              </div>

              <div className="mt-3.5" data-id="element-98" data-mp="38gknsrmav2hp">
                <label htmlFor="details" className="block text-[13.5px] font-semibold text-mist-100" data-id="element-99" data-mp="34ez3il7frlxh">
                  Project details <span className="text-forge-orange" data-id="element-100" data-mp="2zo8tguo2euwg">*</span>
                </label>
                <textarea id="details" name="details" rows={4} required placeholder="Briefly describe what you sell, what needs to improve, and any systems the website needs to connect with." className="mt-2 w-full resize-none rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-[14.5px] leading-relaxed text-white placeholder:text-white/35 focus:border-forge-cyan focus:outline-none focus:ring-2 focus:ring-forge-cyan/30" data-id="element-101" data-mp="2uow9chokw1xk" />
              </div>

              {/* PROTOTYPE ONLY — production must mount the real reCAPTCHA instance here. */}
              <div className="mt-3.5 flex items-center justify-between gap-4 rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3" data-id="element-102" data-mp="222xwdnxi98qw">
                <label className="flex items-center gap-3 text-[13.5px] font-medium text-mist-100" data-id="element-103" data-mp="2ptl5udn2k6fk">
                  <input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} className="h-5 w-5 rounded border-white/30 bg-transparent text-forge-green focus:ring-forge-cyan" data-id="element-104" data-mp="2tt2eth1pflph" />
                  I&apos;m not a robot
                </label>
                <div className="flex shrink-0 items-center gap-2" data-id="element-105" data-mp="3dhuoj6ozke2h">
                  <RefreshCwIcon className="h-4 w-4 text-mist-400" aria-hidden="true" data-id="element-106" data-mp="3dk7ea60zjsq3" />
                  <div className="text-right" data-id="element-107" data-mp="3mv3ide67pvcb">
                    <p className="font-mono text-[12px] uppercase tracking-[0.12em] text-mist-300" data-id="element-108" data-mp="3ivup0af33zha">reCAPTCHA</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-109" data-mp="j6addse6pmh">
                <button type="submit" className="inline-flex items-center justify-center rounded-xl bg-forge-orange px-6 py-3.5 text-[15px] font-bold text-ink shadow-ember transition-transform hover:-translate-y-0.5" data-id="element-110" data-mp="3rpptrhcannwe">
                  Request a Project Conversation
                </button>
                <p className="text-[13px] leading-snug text-mist-300" data-id="element-111" data-mp="38wvq3jk4fi7h">
                  We use your information to respond to your inquiry.
                </p>
              </div>

              {submitted && <p role="status" className="mt-4 flex items-start gap-2.5 rounded-xl border border-forge-green/30 bg-forge-green/10 px-4 py-3.5 text-[13.5px] leading-relaxed text-white" data-id="element-112" data-mp="o5uy4g431cb0">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-forge-green" aria-hidden="true" data-id="element-113" data-mp="2mmnd6tpmzl5p" />
                  Design preview only — nothing was submitted. The live site posts to the existing WebsiteForge form.
                </p>}
            </form>
          </div>
        </motion.div>
      </div>
    </section>;
}
type FieldProps = {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  type?: string;
  required?: boolean;
};
function Field({
  id,
  name,
  label,
  placeholder,
  type = 'text',
  required
}: FieldProps) {
  return <div className="min-w-0" data-id="element-114" data-mp="3fc405buhm9uj">
      <label htmlFor={id} className="block text-[13.5px] font-semibold text-mist-100" data-id="element-115" data-mp="3fbb0euty4klw">
        {label}
        {required && <span className="ml-1 text-forge-orange" data-id="element-116" data-mp="1kze52qsduulk">*</span>}
      </label>
      <input id={id} name={name} type={type} required={required} placeholder={placeholder} className="mt-2 w-full rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-[14.5px] text-white placeholder:text-white/35 focus:border-forge-cyan focus:outline-none focus:ring-2 focus:ring-forge-cyan/30" data-id="element-117" data-mp="1kzmkapbzabzx" />
    </div>;
}